import type {
  IABTestEngine,
  IEngine,
  IEngineMapping,
  IABTestComposition,
  IABTestVariation,
  IVersionEngine,
  IGroupCreate,
  IGroupUpdate,
} from '~/types/arbitration'

import config from '~/config'
import { ENGINE_TYPE } from '~/types/arbitration'
import { request } from '~/utils/request'

export const getEngine = ({
  appKey,
  deviceType,
  engineType,
}: {
  appKey: string
  deviceType: string
  engineType: ENGINE_TYPE
}): Promise<IEngine | IABTestEngine | IVersionEngine[]> => {
  const arbitrationPath = [ENGINE_TYPE.ARCHIVE, ENGINE_TYPE.VERSION].includes(engineType)
    ? `${engineType}/groups`
    : `engine/${engineType}`
  return request({
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/${arbitrationPath}/${appKey}/${deviceType}`,
  })
}

export const updateDevelopmentEngine = ({
  appKey,
  deviceType,
  mapping,
  updatedBy,
}: {
  appKey: string
  deviceType: string
  mapping: IEngineMapping
  updatedBy?: string
}): Promise<void> => {
  return request({
    data: {
      app_key: appKey,
      development: {
        mapping,
        updated_at: new Date(),
        updated_at_ts: Math.floor(new Date().getTime() / 1000),
        updated_by: updatedBy,
      },
      device_type: deviceType,
    },
    method: 'POST',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/update-engine/development`,
  })
}

export const updateReviewEngine = ({
  appKey,
  deviceType,
  mapping,
  updatedBy,
  version,
}: {
  appKey: string
  deviceType: string
  mapping: IEngineMapping
  updatedBy?: string
  version?: string
}): Promise<void> => {
  return request({
    data: {
      app_key: appKey,
      device_type: deviceType,
      review: {
        app_version: version || '',
        mapping,
        updated_at: new Date(),
        updated_at_ts: Math.floor(new Date().getTime() / 1000),
        updated_by: updatedBy,
      },
    },
    method: 'POST',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/update-engine/review`,
  })
}

export const updateABTestEngine = ({
  appKey,
  composition,
  deviceType,
  placement,
  updatedBy,
}: {
  appKey: string
  composition: IABTestComposition
  deviceType: string
  placement: string
  updatedBy?: string
}): Promise<void> => {
  return request({
    data: {
      app_key: appKey,
      device_type: deviceType,
      new_pool: {
        composition,
      },
      placement,
      updated_by: updatedBy,
    },
    method: 'PUT',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/abtest-context/${appKey}/${deviceType}`,
  })
}

export const getPlacementHistory = ({
  appKey,
  deviceType,
  placementId,
}: {
  appKey: string
  deviceType: string
  placementId: string
}): Promise<IABTestVariation[]> => {
  return request({
    data: {
      placement: placementId,
    },
    method: 'POST',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/abtest-context/${appKey}/${deviceType}`,
  })
}

export const updateEngineGroup = ({
  appKey,
  appVersions,
  deviceType,
  engineType,
  force,
  groupId,
  mapping,
  updatedBy,
}: IGroupUpdate & {
  engineType: ENGINE_TYPE.VERSION | ENGINE_TYPE.ARCHIVE
}): Promise<void> => {
  return request({
    data: {
      app_key: appKey,
      app_versions: appVersions,
      device_type: deviceType,
      force,
      mapping,
      updated_by: updatedBy,
    },
    method: 'PUT',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/${engineType}/group/${groupId}`,
  })
}

export const checkEligibility = ({
  appKey,
  appVersions,
  deviceType,
  engineType,
}: {
  appKey: string
  appVersions: string[]
  deviceType: string
  engineType: ENGINE_TYPE.VERSION | ENGINE_TYPE.ARCHIVE
}): Promise<void> => {
  return request({
    data: {
      app_key: appKey,
      app_versions: appVersions,
      device_type: deviceType,
    },
    method: 'POST',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/${engineType}/group/new-version-eligibility`,
  })
}

export const addGroup = ({
  appKey,
  appVersions,
  deviceType,
  engineType,
  force,
  updatedBy,
}: IGroupCreate & {
  engineType: ENGINE_TYPE.VERSION | ENGINE_TYPE.ARCHIVE
}): Promise<{ id: string }> => {
  return request({
    data: {
      app_key: appKey,
      app_versions: appVersions,
      device_type: deviceType,
      force,
      updated_by: updatedBy,
    },
    method: 'POST',
    url: `${config.DYNAMIC_SCREEN_API}/arbitration/${engineType}/group`,
  })
}
